import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api, fmt } from "@/lib/api";
import { toast } from "sonner";

const FREE_SHIPPING = 200;

export default function CartDrawer() {
  const { items, drawerOpen, setDrawerOpen, updateQty, removeItem, subtotal, count } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);

  const checkout = async () => {
    setCheckingOut(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, size: i.size, color: i.color, qty: i.qty })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Checkout failed. Try again.");
      setCheckingOut(false);
    }
  };

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            data-testid="cart-drawer-backdrop"
            className="fixed inset-0 z-[70] bg-black/70"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDrawerOpen(false)}
          />
          <motion.aside
            data-testid="cart-drawer"
            className="fixed right-0 top-0 z-[80] flex h-full w-full max-w-md flex-col border-l border-white/10 bg-ink"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
              <p className="font-mono text-xs tracking-[0.3em] text-bone">CART ({count})</p>
              <button data-testid="cart-drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Close cart" className="text-bone/70 hover:text-bone">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-3">
                  <p className="font-display text-2xl font-bold uppercase text-bone">Your cart is empty</p>
                  <p className="font-mono text-[10px] tracking-[0.3em] text-fade">NOTHING ARRIVES BY ACCIDENT</p>
                </div>
              ) : (
                items.map((i) => (
                  <div key={i.key} data-testid={`cart-item-${i.key}`} className="flex gap-4 border-b border-white/10 py-5">
                    <div className="flex h-24 w-20 shrink-0 items-center justify-center border border-white/10 bg-coal">
                      <span className="font-display text-2xl font-extrabold text-bone/20">{i.angel}</span>
                    </div>
                    <div className="flex flex-1 flex-col">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-display text-xs font-bold uppercase tracking-wide text-bone">{i.name}</p>
                        <button data-testid={`cart-remove-${i.key}`} onClick={() => removeItem(i.key)} aria-label="Remove item" className="text-fade hover:text-bone">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <p className="mt-1 font-mono text-[10px] tracking-widest text-fade">
                        {i.color.toUpperCase()} / {i.size}
                      </p>
                      <div className="mt-auto flex items-center justify-between pt-2">
                        <div className="flex items-center border border-white/15">
                          <button data-testid={`cart-minus-${i.key}`} onClick={() => updateQty(i.key, -1)} aria-label="Decrease quantity" className="px-2 py-1 text-bone/70 hover:text-bone">
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="px-2 font-mono text-xs text-bone">{i.qty}</span>
                          <button data-testid={`cart-plus-${i.key}`} onClick={() => updateQty(i.key, 1)} aria-label="Increase quantity" className="px-2 py-1 text-bone/70 hover:text-bone">
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                        <span className="font-mono text-sm text-bone">{fmt(i.price * i.qty)}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            {items.length > 0 && (
              <div className="border-t border-white/10 px-6 py-5">
                <div className="mb-2 h-px w-full bg-white/10">
                  <div className="h-px bg-bone transition-all duration-700" style={{ width: `${Math.min((subtotal / FREE_SHIPPING) * 100, 100)}%` }} />
                </div>
                <p className="mb-4 font-mono text-[10px] tracking-[0.2em] text-fade">
                  {subtotal >= FREE_SHIPPING ? "FREE SHIPPING UNLOCKED — 444" : `${fmt(FREE_SHIPPING - subtotal)} AWAY FROM FREE SHIPPING`}
                </p>
                <div className="mb-4 flex items-center justify-between">
                  <span className="font-mono text-xs tracking-[0.3em] text-fade">SUBTOTAL</span>
                  <span data-testid="cart-subtotal" className="font-display text-lg font-bold text-bone">
                    {fmt(subtotal)}
                  </span>
                </div>
                <button
                  data-testid="cart-checkout-button"
                  onClick={checkout}
                  disabled={checkingOut}
                  className="w-full bg-bone py-4 font-display text-sm font-extrabold uppercase tracking-[0.2em] text-ink transition-opacity hover:opacity-90 disabled:opacity-50"
                >
                  {checkingOut ? "ALIGNING…" : "CHECKOUT →"}
                </button>
                <p className="mt-3 text-center font-mono text-[9px] tracking-[0.2em] text-fade">SECURE TEST CHECKOUT VIA STRIPE</p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
